// src/Components/Forms/Financement/Salam/Demande/Tab1.tsx
import React, { useMemo } from 'react';
import { Box, Grid, Chip } from '@mui/material';
import { Form } from 'antd';
import InputForm from '../../../../InputT';
import SearchModal from '../../../../SearchModal';
import SearchInput from '../../../../SearchInput';
import SelectOption from '../../../../SelectOption';
import ParamsPicklist from '../../../../../Types/ParamsPicklist';
import { useActifService } from '../../../../Hooks/GetDetailActif';

// Paramètres de recherche
let searchClient: ParamsPicklist = {
  CodeAgence: '',
  Table: "CLIENT",
  Colonne1: "NUMCLI",
  Colonne2: "NOMCLI",
  Colagence: '101',
  Choix: 22
};

let searchNature: ParamsPicklist = {
  CodeAgence: 'ACTIVITE',
  Table: "LIB",
  Colonne1: "CODLIB",
  Colonne2: "LIBELL",
  Colagence: 'RUBRIQ',
  Choix: 22
};

let searchSalam: ParamsPicklist = {
  CodeAgence: '',
  Table: "ParamsSalam",
  Colonne1: "CodeSalam",
  Colonne2: "LibelleSalam",
  Colagence: '101',
  Choix: 22
};

let searchUnite: ParamsPicklist = {
  CodeAgence: '',
  Table: "Unite",
  Colonne1: "CodeUnite",
  Colonne2: "Libelle",
  Colagence: '101',
  Choix: 22
};

let searchObjet: ParamsPicklist = { CodeAgence: 'OBJETFIN', Table: "LIB", Colonne1: "CODLIB", Colonne2: "LIBELL", Colagence: 'RUBRIQ', Choix: 22 }
let searchMotif: ParamsPicklist = { CodeAgence: 'MOTIFPRET', Table: "LIB", Colonne1: "CODLIB", Colonne2: "LIBELL", Colagence: 'RUBRIQ', Choix: 22 }
let searchDemande: ParamsPicklist = {
  CodeAgence: '',
  Table: "DEMAND_FINSALAM",
  Colonne1: "NumeroDemande",
  Colonne2: "NOMCLI",
  Colagence: '101',
  Choix: 22
};

let searchLieuLivraison: ParamsPicklist = {
  CodeAgence: '',
  Table: "PointStockage",
  Colonne1: "CodePoint",
  Colonne2: "Libelle",
  Colagence: '101',
  Choix: 22
};

interface Tab1Props {
  form?: any;
  disabled?: boolean;
  LoadGarantie?: Function;
  onSalamChange?: () => void;
}

/**
 * Retourne la couleur et le libellé du statut en fonction du StatutDemande (ou EtapeActuelle par défaut).
 */
const getStatutStyle = (etape?: string, statutDemande?: string) => {
  const statutUpper = (statutDemande || '').toUpperCase();
  const etapeUpper = (etape || '').toUpperCase();

  switch (statutUpper) {
    case('EN_ATTENTE'):
      return { color: '#faad14', bg: '#fffbe6', label: 'En attente' };
    case('VALIDEE'):
      return { color: '#52c41a', bg: '#f6ffed', label: 'Validée' };
    case('REJET'):
      return { color: '#f5222d', bg: '#fff1f0', label: 'Rejetée' };
    case('EN_ANALYSE'):
      return { color: '#1890ff', bg: '#e6f7ff', label: 'En analyse' };
  }

  return { color: '#8c8c8c', bg: '#f5f5f5', label: 'Non défini' };
};

const Tab1 = (props: Tab1Props) => {
  const NUMCLI = Form.useWatch('NUMCLI', props.form);
  const CodeSalam = Form.useWatch('CodeSalam', props.form);
  const EtapeActuelle = Form.useWatch('EtapeActuelle', props.form);
  const StatutDemande = Form.useWatch('StatutDemande', props.form);

  // Instanciation du hook pour récupérer le coût de l'actif
  const { getCoutInitial } = useActifService();

  const searchActif = useMemo<ParamsPicklist>(() => ({
    CodeAgence: CodeSalam,
    Table: "ViewAnyActifSalam",
    Colonne1: "CodeActif",
    Colonne2: "Designation",
    Colagence: "CodeSalam",
    Choix: 22
  }), [CodeSalam]);

  const searchCompte = useMemo<ParamsPicklist | undefined>(() => {
    if (!NUMCLI || !CodeSalam) return undefined;
    return {
      CodeAgence: NUMCLI,
      Table: "ViewAnySalamCptReglement",
      Colonne1: "NUMCPT",
      Colonne2: "NOMCPT",
      Colagence: "NUMCLI",
      colfiltre: "CodeSalam",
      valfiltre: CodeSalam,
      Choix: 22
    };
  }, [NUMCLI, CodeSalam]);

  const handleClientChange = () => {
    props.form.setFieldValue("CPTREGLEMENT", undefined);
    if (props.LoadGarantie) props.LoadGarantie();
  };

  const handleSalamChange = () => {
    props.form.setFieldValue("CPTREGLEMENT", undefined);
    if (props.onSalamChange) props.onSalamChange();
  };

  const handleActifChange = async () => {
    const codeActif = props.form.getFieldValue('CodeActif');
    if (codeActif) {
      const prix = await getCoutInitial(codeActif);
      if (prix !== null) {
        props.form.setFieldValue('PrixUnitaire', prix);
      }
    }
  };

  // Style du statut (se base désormais sur StatutDemande)
  const statutStyle = getStatutStyle(EtapeActuelle, StatutDemande);

  return (
    <div className="tabItem">
      <Box fontStyle="inherit" className="border" width={"85%"} position={"absolute"}>
        {/* ===== Bandeau d'affichage du statut (visible en consultation) ===== */}
        {props.disabled && (EtapeActuelle || StatutDemande) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 1.5,
              mb: 1,
              bgcolor: statutStyle.bg,
              borderBottom: `2px solid ${statutStyle.color}`,
              borderRadius: '4px 4px 0 0',
            }}
          >
            <Box sx={{ fontWeight: 700, fontSize: 13, color: '#333' }}>
              Statut de la demande :
            </Box>
            <Chip
              label={statutStyle.label}
              sx={{
                bgcolor: statutStyle.color,
                color: '#fff',
                fontWeight: 600,
                fontSize: 12,
                height: 26,
              }}
            />
            <Box sx={{ ml: 'auto', fontSize: 12, color: '#666' }}>
              Étape code : <b>{EtapeActuelle || 'N/A'}</b>
            </Box>
          </Box>
        )}

        <Box className="border-left" p={1}>
          <Grid container spacing={0}>

            {/* Ligne 1 : N° Demande + Type Salam */}
            <Grid item xs={12} md={6}>
              <SearchInput
                width={5}
                Titre="Numéro Demande"
                label="Numéro Demande"
                name="NumeroDemande"
                form={props.form}
                Parametre={searchDemande}
                dissabledCode={!props.disabled}
                notRequired
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SearchModal
                width={5}
                Titre="Recherche Type Salam"
                label="Code Salam"
                name="CodeSalam"
                form={props.form}
                Parametre={searchSalam}
                ExecuteafterSelect2={handleSalamChange}
                disabled={props.disabled}
              />
            </Grid>

            {/* Champs cachés pour EtapeActuelle et StatutDemande (pour Form.useWatch) */}
            <Form.Item name="EtapeActuelle" noStyle>
              <input type="hidden" />
            </Form.Item>
            <Form.Item name="StatutDemande" noStyle>
              <input type="hidden" />
            </Form.Item>

            {/* Ligne 2 : Client + Compte règlement */}
            <Grid item xs={12} md={6}>
              <SearchModal
                width={5}
                Titre="Recherche Client"
                label="Client"
                name="NUMCLI"
                form={props.form}
                Parametre={searchClient}
                ExecuteafterSelect2={handleClientChange}
                disabled={props.disabled || !CodeSalam}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectOption
                width={5}
                label="Compte de règlement"
                name="CPTREGLEMENT"
                Parametre={searchCompte}
                disabled={props.disabled || !NUMCLI || !CodeSalam}
              />
            </Grid>

            {/* Ligne 3 : Activité + Objet */}
            <Grid item xs={12} md={6}>
              <SearchModal
                width={5}
                Titre="Secteur d'activité"
                label="Activité financée"
                name="CODACTIVE"
                form={props.form}
                Parametre={searchNature}
                notRequired
                disabled={props.disabled || !CodeSalam}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SearchModal
                width={5}
                label="Objet de financement"
                Titre="Objet du financement"
                name="CODOBJECT"
                form={props.form}
                Parametre={searchObjet}
                notRequired
                disabled={props.disabled || !CodeSalam}
              />
            </Grid>

            {/* Ligne 4 : Produit + Grade */}
            <Grid item xs={12} md={6}>
              <SearchModal
                width={5}
                Titre="Recherche Produit / Actif"
                label="Actif"
                name="CodeActif"
                form={props.form}
                Parametre={searchActif}
                ExecuteafterSelect2={handleActifChange}
                disabled={props.disabled || !CodeSalam}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputForm
                width={5}
                label="Grade / Qualité"
                name="CommodityGrade"
                notRequired
                type="text"
                disabled
              />
            </Grid>

            {/* Ligne 5 : Unité + Quantité + Prix unitaire */}
            <Grid item xs={12} md={4}>
              <SearchModal
                width={7}
                Titre="Recherche Unité"
                label="Unité"
                name="Unite"
                form={props.form}
                Parametre={searchUnite}
                disabled={props.disabled || !CodeSalam}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <InputForm
                width={7}
                label="Quantité"
                name="Quantite"
                type="number"
                money
                disabled={props.disabled || !CodeSalam}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <InputForm
                width={7}
                label="Prix unitaire"
                name="PrixUnitaire"
                type="number"
                money
                disabled={props.disabled || !CodeSalam}
              />
            </Grid>

            {/* Ligne 6 : Montant total + Frais de service */}
            <Grid item xs={12} md={6}>
              <InputForm
                width={7}
                label="Montant total"
                name="MontantDemande"
                type="number"
                money
                disabled={true}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputForm
                width={7}
                label="Frais de service"
                name="FraisService"
                notRequired
                type="number"
                money
                disabled={true}
              />
            </Grid>

            {/* Ligne 7 : Date livraison + Lieu de livraison */}
            <Grid item xs={12} md={6}>
              <InputForm
                width={7}
                label="Date de livraison prévue"
                name="DateLivraison"
                type="date"
                disabled={props.disabled || !CodeSalam}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SearchModal
                width={7}
                label="Lieu de livraison"
                Titre="Recherche Lieu de livraison"
                form={props.form}
                name="LieuLivraison"
                notRequired
                Parametre={searchLieuLivraison}
                disabled={props.disabled || !CodeSalam}
              />
            </Grid>

            {/* Ligne 8 : Commentaire */}
            <Grid item xs={12} md={7}>
              <InputForm
                width={4}
                label="Commentaire"
                name="CommentaireDemande"
                type="TextArea"
                disabled={props.disabled || !CodeSalam}
              />
            </Grid>

          </Grid>
        </Box>
      </Box>
    </div>
  );
};

export default Tab1;